import { X, Save } from 'lucide-react';
import { useState } from 'react';

interface Event {
  id: string;
  title?: string;
  name?: string;
  date?: string;
  description?: string;
  location?: string;
  venue?: string;
}

interface EditEventModalProps {
  editingEventId: string | null;
  setEditingEventId: (id: string | null) => void;
  event: Event | null;
  onSave: (eventId: string, eventData: any) => Promise<void>;
}

export function EditEventModal({ editingEventId, setEditingEventId, event, onSave }: EditEventModalProps) {
  const [formData, setFormData] = useState({
    name: event?.title || event?.name || '',
    date: event?.date || '',
    description: event?.description || '',
    location: event?.location || event?.venue || '',
    venue: event?.venue || event?.location || ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingEventId) {
      await onSave(editingEventId, formData);
    }
  };

  if (!editingEventId) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-gray-100">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gradient-primary">Modifica Evento</h2>
            <button
              onClick={() => setEditingEventId(null)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nome Evento</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/15 transition-all outline-none"
              placeholder="Nome dell'evento"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Data</label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/15 transition-all outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Luogo</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/15 transition-all outline-none"
              placeholder="Es: Fiera di Verifica, Padiglione 1"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Venue/Sede</label>
            <input
              type="text"
              value={formData.venue}
              onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
              className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:shadow-lg focus:shadow-blue-500/15 transition-all outline-none"
              placeholder="Es: Centro Congressi"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#676767] focus:border-transparent transition-all outline-none min-h-[100px] resize-none"
              placeholder="Descrizione completa dell'evento..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setEditingEventId(null)}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-bold hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              Salva Modifiche
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
